import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { useCreateService } from "@/hooks/useServices";
import { useVendors } from "@/hooks/useVendors";
import { useServiceCategories, useServiceSubCategories, useServiceTypes } from "@/hooks/useServiceCategories";
import type { CreateServiceRequest } from "@/types/services";

interface CreateServiceModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CreateServiceModal({ open, onClose}: CreateServiceModalProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<CreateServiceRequest>({
    defaultValues: {
      isActive: true
    }
  });

  const createService = useCreateService();
  const { vendors, isLoading: vendorsLoading } = useVendors();
  const { data: categories, isLoading: categoriesLoading } = useServiceCategories();
  const { data: subcategories, isLoading: subcategoriesLoading } = useServiceSubCategories(selectedCategoryId);
  const { data: serviceTypes, isLoading: serviceTypesLoading } = useServiceTypes();

  const selectedVendorId = watch("vendorId");
  const selectedServiceTypeId = watch("serviceTypeId");
  const selectedServiceCategoryId = watch("serviceCategoryId");
  const selectedServiceSubCategoryId = watch("serviceSubCategoryId");
  const isActive = watch("isActive");

  // Reset subcategory selection when category changes
  useEffect(() => {
    if (selectedServiceCategoryId !== selectedCategoryId) {
      setSelectedCategoryId(selectedServiceCategoryId);
      setValue("serviceSubCategoryId", "");
    }
  }, [selectedServiceCategoryId, selectedCategoryId, setValue]);

  const onSubmit = async (data: CreateServiceRequest) => {
    try {
      await createService.mutateAsync(data);
      toast({
        title: "Service created",
        description: "Service has been created successfully.",
      });
      reset();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create service. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    reset();
    setSelectedCategoryId("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Service</DialogTitle>
          <DialogDescription>
            Create a new marketplace service with the specified vendor, category, and type.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Basic Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Service Name *</Label>
                <Input
                  id="name"
                  {...register("name", { required: "Service name is required" })}
                  placeholder="Enter service name"
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor *</Label>
                <Select
                  value={selectedVendorId || ""}
                  onValueChange={(value) => setValue("vendorId", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendorsLoading ? (
                      <SelectItem value="loading" disabled>Loading vendors...</SelectItem>
                    ) : (
                      vendors?.map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.vendorId && (
                  <p className="text-sm text-destructive">{errors.vendorId.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Enter service description"
                rows={3}
              />
            </div>
          </div>

          {/* Classification */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Service Classification</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Service Category *</Label>
                <Select
                  value={selectedServiceCategoryId || ""}
                  onValueChange={(value) => setValue("serviceCategoryId", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesLoading ? (
                      <SelectItem value="loading" disabled>Loading categories...</SelectItem>
                    ) : (
                      categories?.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.serviceCategoryId && (
                  <p className="text-sm text-destructive">{errors.serviceCategoryId.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subcategory">Service Subcategory *</Label>
                <Select
                  value={selectedServiceSubCategoryId || ""}
                  onValueChange={(value) => setValue("serviceSubCategoryId", value)}
                  disabled={!selectedServiceCategoryId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={selectedServiceCategoryId ? "Select subcategory" : "Select category first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategoriesLoading ? (
                      <SelectItem value="loading" disabled>Loading subcategories...</SelectItem>
                    ) : (
                      subcategories?.map((subcategory) => (
                        <SelectItem key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.serviceSubCategoryId && (
                  <p className="text-sm text-destructive">{errors.serviceSubCategoryId.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="serviceType">Service Type *</Label>
              <Select
                value={selectedServiceTypeId || ""}
                onValueChange={(value) => setValue("serviceTypeId", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypesLoading ? (
                    <SelectItem value="loading" disabled>Loading service types...</SelectItem>
                  ) : (
                    serviceTypes?.map((serviceType) => (
                      <SelectItem key={serviceType.id} value={serviceType.id}>
                        {serviceType.name}
                        <span className="text-muted-foreground ml-2">({serviceType.behaviorType})</span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.serviceTypeId && (
                <p className="text-sm text-destructive">{errors.serviceTypeId.message}</p>
              )}
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Additional Information</h3>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="Enter any additional notes"
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={isActive}
                onCheckedChange={(checked) => setValue("isActive", checked)}
              />
              <Label htmlFor="isActive" className="text-sm font-medium">
                Active
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createService.isPending}>
              {createService.isPending ? "Creating..." : "Create Service"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}